import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  Chip,
  Alert,
  InputAdornment,
  Tooltip,
  Divider,
  Stack
} from '@mui/material';
import {
  Lock as LockIcon,
  LockOpen as LockOpenIcon,
  Delete as DeleteIcon,
  Add as AddIcon
} from '@mui/icons-material';
import TagSelector from './TagSelector';
import { formatMoney, currencySymbol, localeFor } from '../utils/money';
import { categoryMenuItems } from '../utils/categoryMenu';

function SplitTransactionAmountDialog({ open, transaction, categories, tags, onClose, onSplit }) {
  const [splits, setSplits] = useState([]);
  const [allLocked, setAllLocked] = useState(false);
  const [error, setError] = useState('');

  // Initialize splits when dialog opens
  useEffect(() => {
    if (open && transaction) {
      const halfAmount = (transaction.amount / 2).toFixed(2);
      setSplits([
        {
          id: '1',
          amount: halfAmount,
          category: transaction.category || '',
          tags: transaction.tags || [],
          locked: false
        },
        {
          id: '2',
          amount: halfAmount,
          category: transaction.category || '',
          tags: transaction.tags || [],
          locked: true
        }
      ]);
      setAllLocked(false);
      setError('');
    }
  }, [open, transaction]);

  const getLockedCount = () => splits.filter(s => s.locked).length;
  const getUnlockedCount = () => splits.filter(s => !s.locked).length;

  const redistributeToLocked = (newSplits) => {
    const unlockedTotal = newSplits
      .filter(s => !s.locked)
      .reduce((sum, s) => sum + parseFloat(s.amount || 0), 0);
    
    const remaining = transaction.amount - unlockedTotal;
    const lockedCount = getLockedCount();
    
    if (lockedCount > 0) {
      const amountPerLocked = (remaining / lockedCount).toFixed(2);
      
      return newSplits.map(split => {
        if (split.locked) {
          return { ...split, amount: amountPerLocked };
        }
        return split;
      });
    }
    
    return newSplits;
  };

  const handleAmountChange = (splitId, newAmount) => {
    const numValue = parseFloat(newAmount) || 0;
    
    let newSplits = splits.map(split =>
      split.id === splitId ? { ...split, amount: newAmount } : split
    );
    
    // Redistribute to locked splits
    newSplits = redistributeToLocked(newSplits);
    setSplits(newSplits);
  };

  const handleCategoryChange = (splitId, category) => {
    setSplits(splits.map(split =>
      split.id === splitId ? { ...split, category } : split
    ));
  };

  const handleTagsChange = (splitId, newTags) => {
    setSplits(splits.map(split =>
      split.id === splitId ? { ...split, tags: newTags } : split
    ));
  };

  const handleToggleLock = (splitId) => {
    const split = splits.find(s => s.id === splitId);
    const lockedCount = getLockedCount();
    
    // Can't unlock if it's the only locked split
    if (split.locked && lockedCount === 1) {
      setError('At least one split must remain locked');
      return;
    }
    
    let newSplits = splits.map(s =>
      s.id === splitId ? { ...s, locked: !s.locked } : s
    );
    
    // Redistribute to locked splits
    newSplits = redistributeToLocked(newSplits);
    setSplits(newSplits);
    setError('');
  };

  const handleLockAll = () => {
    // Lock all splits
    let newSplits = splits.map(s => ({ ...s, locked: true }));
    
    // Then unlock the last one
    if (newSplits.length > 0) {
      newSplits[newSplits.length - 1].locked = false;
    }
    
    newSplits = redistributeToLocked(newSplits);
    setSplits(newSplits);
    setAllLocked(true);
    setError('');
  };

  const handleAddSplit = () => {
    const newId = Date.now().toString();
    const lockedCount = getLockedCount();
    const remaining = transaction.amount - splits
      .filter(s => !s.locked)
      .reduce((sum, s) => sum + parseFloat(s.amount || 0), 0);
    
    const amountPerLocked = (remaining / (lockedCount + 1)).toFixed(2);
    
    const newSplit = {
      id: newId,
      amount: amountPerLocked,
      category: transaction.category || '',
      tags: transaction.tags || [],
      locked: true
    };
    
    let newSplits = [...splits, newSplit];
    newSplits = redistributeToLocked(newSplits);
    setSplits(newSplits);
  };

  const handleDeleteSplit = (splitId) => {
    const split = splits.find(s => s.id === splitId);
    const lockedCount = getLockedCount();
    
    // Can't delete if only 1 split remains
    if (splits.length === 1) {
      setError('Cannot delete the last split');
      return;
    }
    
    // Can't delete if it's the only locked split
    if (split.locked && lockedCount === 1) {
      setError('Cannot delete the only locked split');
      return;
    }
    
    let newSplits = splits.filter(s => s.id !== splitId);
    newSplits = redistributeToLocked(newSplits);
    setSplits(newSplits);
    setError('');
  };

  const getTotalAmount = () => {
    return splits.reduce((sum, split) => sum + parseFloat(split.amount || 0), 0).toFixed(2);
  };

  const isValid = () => {
    if (splits.length < 1) return false;
    
    // Check all splits have category
    if (splits.some(s => !s.category)) return false;
    
    // Check all amounts are positive
    if (splits.some(s => !s.amount || parseFloat(s.amount) <= 0)) return false;
    
    // Check total matches (allow 0.01 difference for rounding)
    const total = parseFloat(getTotalAmount());
    return Math.abs(total - transaction.amount) < 0.02;
  };

  const handleSplit = () => {
    if (!isValid()) {
      setError('Please fix validation errors before splitting');
      return;
    }
    
    const splitData = splits.map(split => ({
      amount: parseFloat(split.amount),
      category: split.category,
      tags: split.tags
    }));
    
    onSplit(splitData);
  };

  if (!transaction) return null;

  const totalAmount = getTotalAmount();
  const isBalanced = Math.abs(parseFloat(totalAmount) - transaction.amount) < 0.02;

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)'
        }
      }}
    >
      <DialogTitle sx={{ 
        borderBottom: '1px solid',
        borderColor: 'divider',
        pb: 2
      }}>
        <Typography variant="h5" component="div" fontWeight={600}>
          Split Transaction
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Divide this transaction into multiple transactions with different amounts and categories
        </Typography>
      </DialogTitle>
      
      <DialogContent sx={{ pt: 3 }}>
        {/* Original Transaction Info */}
        <Box sx={{ 
          mb: 3, 
          p: 2.5, 
          bgcolor: 'linear-gradient(135deg, rgba(6, 182, 212, 0.08) 0%, rgba(6, 182, 212, 0.02) 100%)',
          border: '1px solid',
          borderColor: 'rgba(6, 182, 212, 0.2)',
          borderRadius: 2 
        }}>
          <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 600, letterSpacing: 1 }}>
            Original Transaction
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', mt: 1 }}>
            <Typography variant="h5" sx={{ fontWeight: 700, color: 'primary.main' }}>
              {formatMoney(transaction.amount)}
            </Typography>
            <Chip 
              label={transaction.category} 
              size="small" 
              sx={{ 
                bgcolor: 'primary.main', 
                color: 'white',
                fontWeight: 600
              }} 
            />
            <Typography variant="body2" color="text.secondary">
              •
            </Typography>
            <Typography variant="body2" color="text.secondary" fontWeight={500}>
              {new Date(transaction.date).toLocaleDateString(localeFor(), { 
                day: 'numeric', 
                month: 'short', 
                year: 'numeric' 
              })}
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5, fontStyle: 'italic' }}>
            "{transaction.description}"
          </Typography>
          {transaction.tags && transaction.tags.length > 0 && (
            <Box sx={{ display: 'flex', gap: 0.5, mt: 1.5, flexWrap: 'wrap' }}>
              {transaction.tags.map(tag => (
                <Chip 
                  key={tag.id} 
                  label={tag.name} 
                  size="small"
                  sx={{ 
                    bgcolor: tag.color || 'default',
                    color: 'white',
                    fontSize: '0.7rem'
                  }}
                />
              ))}
            </Box>
          )}
        </Box>

        {/* Split Details Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2.5 }}>
          <Box>
            <Typography variant="h6" fontWeight={600}>
              Split Details
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Configure how to split this transaction
            </Typography>
          </Box>
          <Button
            size="small"
            variant="outlined"
            startIcon={<LockIcon />}
            onClick={handleLockAll}
            sx={{ 
              borderRadius: 1.5,
              textTransform: 'none',
              fontWeight: 600
            }}
          >
            Lock All
          </Button>
        </Box>

        {/* Error Message */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {/* Splits */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {splits.map((split, index) => (
            <Box
              key={split.id}
              sx={{
                p: 2.5,
                border: '2px solid',
                borderColor: split.locked ? 'primary.main' : 'divider',
                borderRadius: 2,
                bgcolor: split.locked ? 'rgba(6, 182, 212, 0.05)' : 'background.paper',
                transition: 'all 0.2s ease',
                '&:hover': {
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                  transform: 'translateY(-2px)'
                }
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="subtitle1" fontWeight={700}>
                    Split {index + 1}
                  </Typography>
                  {split.locked && (
                    <Chip 
                      label="Auto" 
                      size="small" 
                      color="primary"
                      sx={{ height: 20, fontSize: '0.7rem', fontWeight: 600 }}
                    />
                  )}
                </Box>
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  <Tooltip title={split.locked ? 'Unlock to edit amount' : 'Lock for auto-calculation'}>
                    <IconButton
                      size="small"
                      onClick={() => handleToggleLock(split.id)}
                      sx={{
                        bgcolor: split.locked ? 'primary.main' : 'action.hover',
                        color: split.locked ? 'white' : 'text.secondary',
                        '&:hover': {
                          bgcolor: split.locked ? 'primary.dark' : 'action.selected',
                        }
                      }}
                    >
                      {split.locked ? <LockIcon fontSize="small" /> : <LockOpenIcon fontSize="small" />}
                    </IconButton>
                  </Tooltip>
                  {splits.length > 1 && (
                    <Tooltip title="Delete split">
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteSplit(split.id)}
                        sx={{
                          bgcolor: 'error.lighter',
                          color: 'error.main',
                          '&:hover': {
                            bgcolor: 'error.light',
                          }
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              </Box>

              <Stack spacing={2}>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <TextField
                    label="Amount"
                    type="number"
                    value={split.amount}
                    onChange={(e) => handleAmountChange(split.id, e.target.value)}
                    disabled={split.locked}
                    fullWidth
                    InputProps={{
                      startAdornment: <InputAdornment position="start">{currencySymbol()}</InputAdornment>,
                    }}
                    inputProps={{
                      step: '0.01',
                      min: '0'
                    }}
                    helperText={split.locked ? 'Auto-calculated' : ''}
                    sx={{ flex: 1 }}
                  />
                  <FormControl fullWidth sx={{ flex: 1 }}>
                    <InputLabel>Category</InputLabel>
                    <Select
                      value={split.category}
                      onChange={(e) => handleCategoryChange(split.id, e.target.value)}
                      label="Category"
                    >
                      {categoryMenuItems(categories)}
                    </Select>
                  </FormControl>
                </Box>
                
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                    Tags
                  </Typography>
                  <TagSelector
                    allTags={tags || []}
                    value={split.tags || []}
                    onChange={(newTags) => handleTagsChange(split.id, newTags)}
                    label=""
                    placeholder="Add tags..."
                  />
                </Box>
              </Stack>
            </Box>
          ))}
        </Box>

        {/* Add Split Button */}
        <Button
          fullWidth
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={handleAddSplit}
          sx={{ 
            mt: 2.5,
            borderRadius: 1.5,
            borderStyle: 'dashed',
            borderWidth: 2,
            py: 1.5,
            textTransform: 'none',
            fontWeight: 600,
            '&:hover': {
              borderStyle: 'dashed',
              borderWidth: 2,
              bgcolor: 'action.hover'
            }
          }}
        >
          Add Another Split
        </Button>

        {/* Total Display */}
        <Box
          sx={{
            mt: 3,
            p: 2.5,
            bgcolor: isBalanced 
              ? 'linear-gradient(135deg, rgba(76, 175, 80, 0.08) 0%, rgba(76, 175, 80, 0.02) 100%)'
              : 'linear-gradient(135deg, rgba(244, 67, 54, 0.08) 0%, rgba(244, 67, 54, 0.02) 100%)',
            borderRadius: 2,
            border: '2px solid',
            borderColor: isBalanced ? 'success.main' : 'error.main'
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, letterSpacing: 0.5 }}>
                TOTAL SPLIT AMOUNT
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {isBalanced ? 'Perfectly balanced!' : 'Amount mismatch'}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box sx={{ textAlign: 'right' }}>
                <Typography variant="h5" fontWeight={700} color={isBalanced ? 'success.main' : 'error.main'}>
                  {formatMoney(totalAmount)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  of {formatMoney(transaction.amount)}
                </Typography>
              </Box>
              <Box sx={{ 
                width: 40, 
                height: 40, 
                borderRadius: '50%',
                bgcolor: isBalanced ? 'success.main' : 'error.main',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <Typography variant="h6" sx={{ color: 'white', fontWeight: 700 }}>
                  {isBalanced ? '✓' : '!'}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ 
        px: 3, 
        pb: 2.5, 
        pt: 2,
        borderTop: '1px solid',
        borderColor: 'divider',
        gap: 1.5
      }}>
        <Button 
          onClick={onClose}
          variant="outlined"
          sx={{ 
            borderRadius: 1.5,
            textTransform: 'none',
            fontWeight: 600,
            px: 3
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSplit}
          variant="contained"
          disabled={!isValid()}
          sx={{ 
            borderRadius: 1.5,
            textTransform: 'none',
            fontWeight: 600,
            px: 3,
            boxShadow: '0 4px 12px rgba(6, 182, 212, 0.3)',
            '&:hover': {
              boxShadow: '0 6px 16px rgba(6, 182, 212, 0.4)',
            }
          }}
        >
          Split Transaction into {splits.length} Parts
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default SplitTransactionAmountDialog;

